// frontend/src/App.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Header from "./components/Header";
import Footer from "./components/Footer";
import SearchSection from "./components/SearchSection";
import SchoolsSection from "./components/SchoolsSection";
import LoadingSpinner from "./components/LoadingSpinner";
import ErrorMessage from "./components/ErrorMessage";
import { useAuth } from "./lib/auth";
import { useSearchContext } from "./lib/searchContext";
import { addDistanceAndSort, haversineMiles } from "./lib/distance";
import MoreToolsToTry from "./components/MoreToolsToTry";
import {
  HeroSection,
  CompareSection,
  HomeSearchSection,
  ShareStorySection,
  ProcessSection,
} from "./components/LandingSections";
import { schoolAPI } from "./services/api";

const DEFAULT_COORDINATES = [40.7589, -73.9851];
const ITEMS_PER_PAGE = 10; 

// --- UTILITIES ---
const STATE_QUERY_ALIASES = {
  ca: ["ca", "california"],
  az: ["az", "arizona"],
  il: ["il", "illinois"],
  fl: ["fl", "florida"],
  ky: ["ky", "kentucky"],
  mn: ["mn", "minnesota"],
  mi: ["mi", "michigan"],
  wy: ["wy", "wyoming"],
  tx: ["tx", "texas"],
  al: ["al", "alabama"],
  nj: ["nj", "new jersey"],
  nc: ["nc", "north carolina"],
  oh: ["oh", "ohio"],
  wv: ["wv", "west virginia"],
};

const normalizeLocationQuery = (input) => {
  if (!input) return input;
  const trimmed = input.trim();
  const cleaned = trimmed.replace(/[,\.\s]+$/g, "").toLowerCase();
  if (!cleaned) return "";
  for (const [abbr, aliases] of Object.entries(STATE_QUERY_ALIASES)) {
    if (aliases.includes(cleaned)) return abbr;
  }
  return cleaned;
};

const toTitleCase = (value = "") =>
  value
    .replace(/\uFFFD/g, "")
    .split(/\s+/)
    .map((word) => {
      if (!word) return "";
      const cleaned = word.replace(/[^a-zA-Z]/g, "");
      if (cleaned.length === 2) return word.toUpperCase();
      return word[0].toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ")
    .replace(/\s{2,}/g, " ")
    .trim();

const inferSearchSource = (query) => {
  const raw = (query || "").trim();
  if (!raw) return "city";
  const digitsOnly = raw.replace(/[^0-9]/g, "");
  const hasLetters = /[a-zA-Z]/.test(raw);
  if (!hasLetters && digitsOnly.length >= 5) return "zip";
  if (/\d/.test(raw)) return "address";
  const normalized = normalizeLocationQuery(raw);
  const stateMatches = Object.keys(STATE_QUERY_ALIASES).includes(normalized);
  if (stateMatches) return "state";
  return "city";
};

const sanitizeSchoolName = (value) => {
  if (!value) return value;
  return String(value).replace(/\uFFFD/g, "").replace(/\s{2,}/g, " ").trim();
};

const fallbackMetrics = {
  college_readiness_score: null,
  academic_preparation: null,
  college_enrollment: null,
  academic_performance: null,
};

// --- DATA NORMALIZATION ---
const normalizeSchool = (school, { source, userCoords }) => {
  const rawMetrics = school.metrics ?? {};
  const coordinates = school.coordinates ?? {};
  const address = school.address ?? {};
  const percentMultiplier = source === "athletes" ? 100 : 1;
  const snapgradRating =
    school.Snapgrad_rating ??
    school.snapgrad_rating ??
    rawMetrics.Snapgrad_rating ??
    rawMetrics.snapgrad_rating ??
    null;
  const latitudeRaw =
    coordinates.latitude ?? school.latitude ?? school.lat ?? null;
  const longitudeRaw =
    coordinates.longitude ?? school.longitude ?? school.lon ?? null;
  const latitudeValue =
    latitudeRaw === null || latitudeRaw === undefined
      ? null
      : Number(latitudeRaw);
  const longitudeValue =
    longitudeRaw === null || longitudeRaw === undefined
      ? null
      : Number(longitudeRaw);

  const hasCoords =
    Number.isFinite(latitudeValue) && Number.isFinite(longitudeValue);

  const distance_miles =
    hasCoords && userCoords && typeof userCoords.latitude === "number"
      ? haversineMiles(
          userCoords.latitude,
          userCoords.longitude,
          latitudeValue,
          longitudeValue
        )
      : null;

  const metrics = { ...rawMetrics };
  ["college_enrollment", "graduation_rate", "college_performance"].forEach(
    (key) => {
      if (metrics[key] !== undefined && metrics[key] !== null) {
        metrics[key] = Number(metrics[key]) * percentMultiplier;
      }
    }
  );

  const latitude = hasCoords ? latitudeValue : DEFAULT_COORDINATES[0];
  const longitude = hasCoords ? longitudeValue : DEFAULT_COORDINATES[1];

  return {
    id: school.id,
    school_name: sanitizeSchoolName(school.school_name),
    address_address: address.street ?? "",
    address_city: address.city ?? "",
    address_state: address.state ?? "",
    address_zipcode: address.zipcode ?? "",
    address,
    coordinates,
    top_colleges: school.top_colleges ?? [],
    top_majors: school.top_majors ?? school.metrics?.top_majors ?? [],
    latitude,
    longitude,
    distance_miles: distance_miles,
    Snapgrad_rating: snapgradRating,
    metrics: {
      ...metrics,
      college_readiness_score:
        metrics.college_readiness_score ?? metrics.act_average ?? null,
      graduation_rate:
        metrics.graduation_rate ?? metrics.college_performance ?? null,
      Snapgrad_rating: snapgradRating,
    },
    college_readiness_score:
      metrics.college_readiness_score ?? metrics.act_average ?? null,
    college_preparation: metrics.college_preparation ?? null,
    college_enrollment: metrics.college_enrollment ?? null,
    total_students: metrics.total_students ?? school.total_students ?? null,
    grade_academics: school.grade_academics ?? null,
    graduation_rate: metrics.graduation_rate
      ? metrics.graduation_rate / 100
      : metrics.college_performance
      ? metrics.college_performance / 100
      : null,
    graduation_rate_percent:
      metrics.graduation_rate ?? metrics.college_performance ?? null,
    sat_average: metrics.sat_average ?? null,
    act_average: metrics.act_average ?? null,
    math_proficiency: metrics.math_proficiency
      ? metrics.math_proficiency / 100
      : null,
    reading_proficiency: metrics.reading_proficiency
      ? metrics.reading_proficiency / 100
      : null,
    school_type: school.school_type ?? null,
    is_public: school.school_type === "Public",
    is_charter: school.school_type === "Charter",
    is_high: school.is_high ?? true,
    source,
  };
};

const transformSchoolList = (
  rawSchools,
  query,
  { source = "overview", userLocation, totalOverride, metricsOverride } = {}
) => {
  const userCoords = userLocation?.coordinates;
  const userZip = (userLocation?.zip || "").replace(/[^0-9]/g, "");
  const normalizedQuery = (query || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const isAustinOnlyQuery = source === "overview" && normalizedQuery === "austin";

  const normalizedSchools = rawSchools.map((school) =>
    normalizeSchool(school, { source, userCoords })
  );

  const seenKeys = new Set();
  const schools = normalizedSchools.filter((school) => {
    const key =
      school.id ??
      `${school.school_name}|${school.address_city}|${school.address_state}|${school.address_zipcode}`
        .toLowerCase()
        .trim();
    if (!key) return true;
    if (seenKeys.has(key)) return false;
    seenKeys.add(key);
    return true;
  });

  const shouldPrioritizeRatings = source === "overview";

  const hasSnapgradRating = (school) => {
    const raw =
      school?.Snapgrad_rating ??
      school?.snapgrad_rating ??
      school?.metrics?.Snapgrad_rating ??
      school?.metrics?.snapgrad_rating ??
      null;
    const num = Number(raw);
    return Number.isFinite(num) && num > 0;
  };

  const prioritizeSnapgradRating = (list) =>
    list.sort((a, b) => {
      const aHas = hasSnapgradRating(a) ? 1 : 0;
      const bHas = hasSnapgradRating(b) ? 1 : 0;
      return bHas - aHas;
    });

  let orderedSchools = schools;

  if (userCoords || userZip) {
    const zipMatches = [];
    const nearby = [];
    const others = [];
    const radiusLimit = userLocation?.radius ?? 35;

    schools.forEach((school) => {
      const schoolZip = (school.address_zipcode || "").replace(/[^0-9]/g, "");
      const isZipMatch = userZip && schoolZip && schoolZip === userZip;
      const isNearby =
        school.distance_miles !== null && school.distance_miles <= radiusLimit;

      if (isZipMatch) {
        zipMatches.push(school);
      } else if (isNearby) {
        nearby.push(school);
      } else {
        others.push(school);
      }
    });

    nearby.sort(
      (a, b) => (a.distance_miles ?? Infinity) - (b.distance_miles ?? Infinity)
    );
    orderedSchools = shouldPrioritizeRatings
      ? [
          ...prioritizeSnapgradRating(zipMatches),
          ...prioritizeSnapgradRating(nearby),
          ...prioritizeSnapgradRating(others),
        ]
      : [...zipMatches, ...nearby, ...others];
  } else if (shouldPrioritizeRatings) {
    orderedSchools = prioritizeSnapgradRating(orderedSchools);
  }

  if (isAustinOnlyQuery && !userCoords && !userZip) {
    const austinTx = [];
    const remaining = [];
    orderedSchools.forEach((school) => {
      const city = (school.address_city || "").toString().trim().toLowerCase();
      const state = (school.address_state || "").toString().trim().toLowerCase();
      if (city === "austin" && state === "tx") {
        austinTx.push(school);
      } else {
        remaining.push(school);
      }
    });
    orderedSchools = [...austinTx, ...remaining];
  }

  return {
    query,
    totalSchools: totalOverride ?? orderedSchools.length,
    metrics: metricsOverride ?? fallbackMetrics,
    coordinates:
      orderedSchools.length > 0
        ? [orderedSchools[0].latitude, orderedSchools[0].longitude]
        : DEFAULT_COORDINATES,
    schools: orderedSchools,
  };
};

// Helper function to scroll to results smoothly
const scrollToResults = () => {
  requestAnimationFrame(() => {
    const resultsElement = document.getElementById("results-anchor");
    if (resultsElement) {
      resultsElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  });
};

const scrollToSearch = () => {
  requestAnimationFrame(() => {
    const searchElement = document.getElementById("search");
    if (searchElement) {
      searchElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  });
};

function App() {
  const { searchContext, setSearchContext } = useSearchContext();
  const [searchResults, setSearchResults] = useState(null);
  const [isSearched, setIsSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentQuery, setCurrentQuery] = useState("");
  const [userLocation, setUserLocation] = useState(null);
  const [activeView, setActiveView] = useState("overview");
  const [authModalOpen, setAuthModalOpen] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);

  // Secondary views
  const [stemResults, setStemResults] = useState(null);
  const [stemLoading, setStemLoading] = useState(false);
  const [stemError, setStemError] = useState(null);
  const [athletesResults, setAthletesResults] = useState(null);
  const [athletesLoading, setAthletesLoading] = useState(false);
  const [athletesError, setAthletesError] = useState(null);
  const [locationError, setLocationError] = useState(null);

  const autoScrolledRef = useRef(false);
  const searchRequestRef = useRef(0);
  const abortControllerRef = useRef(null);
  const pageCacheRef = useRef(new Map());

  const { isSignedIn } = useAuth();

  const openAuthModal = useCallback(() => setAuthModalOpen(true), []);
  const closeAuthModal = useCallback(() => setAuthModalOpen(false), []);

  // --- CORE FETCH HELPER ---
  const fetchPageData = useCallback(
    async (pageNumber, queryStr, locationData, signal = null) => {
      const normalizedQuery = normalizeLocationQuery(queryStr);
      const baseOptions = {
        latitude: locationData?.coordinates?.latitude,
        longitude: locationData?.coordinates?.longitude,
        radius_miles: locationData?.radius || 35,
        limit: ITEMS_PER_PAGE,
        offset: (pageNumber - 1) * ITEMS_PER_PAGE,
      };

      const searchFn = isSignedIn
        ? schoolAPI.searchSchools
        : schoolAPI.searchSchoolsPublic;

      const response = await searchFn(normalizedQuery, baseOptions, { signal });

      if (!response?.success || !response?.data) {
        throw new Error("No data returned");
      }

      const pageSchools = response.data.schools || [];
      const totalEstimate =
        response.data.total_schools || pageSchools.length;
      const metrics = response.data.metrics;

      return transformSchoolList(pageSchools, queryStr, {
        source: "overview",
        userLocation: locationData,
        totalOverride: totalEstimate,
        metricsOverride: metrics,
      });
    },
    [isSignedIn]
  );

  // --- AGGRESSIVE BACKGROUND PREFETCHER ---
  useEffect(() => {
    if (!isSearched || !currentQuery || activeView !== "overview") return;
    
    const totalSchools = searchResults?.totalSchools || 0;
    const totalPages = Math.ceil(totalSchools / ITEMS_PER_PAGE);
    
    const pagesToPrefetch = [currentPage + 1, currentPage + 2];

    pagesToPrefetch.forEach((pageNum) => {
      if (pageNum <= totalPages && !pageCacheRef.current.has(pageNum)) {
        fetchPageData(pageNum, currentQuery, userLocation)
          .then((data) => {
            pageCacheRef.current.set(pageNum, data);
          })
          .catch((err) => {
             // Ignore background errors
          });
      }
    });
  }, [currentPage, isSearched, currentQuery, userLocation, searchResults, activeView, fetchPageData]);

  const handleSearch = async (searchInput) => {
    const requestId = searchRequestRef.current + 1;
    searchRequestRef.current = requestId;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort("New search started");
    }
    const newController = new AbortController();
    abortControllerRef.current = newController;

    const normalizedInput =
      typeof searchInput === "string" ? { query: searchInput } : searchInput || {};
    const address = (normalizedInput.query || "").trim();
    if (!address) return;

    autoScrolledRef.current = false;
    setActiveView("overview");
    setIsLoading(true);
    setError(null);
    setLocationError(null);
    setCurrentQuery(address);
    setCurrentPage(1);
    pageCacheRef.current.clear();

    const coordinates = normalizedInput.coordinates;
    const radiusMiles = normalizedInput.radiusMiles ?? 35;
    const zipFromGeolocation = normalizedInput.zipFromGeolocation;
    const digitsOnly = address.replace(/[^0-9]/g, "");
    const looksLikeZip = digitsOnly.length >= 5 && !/[a-zA-Z]/.test(address);
    const source = inferSearchSource(address);
    const label = looksLikeZip ? digitsOnly.slice(0, 5) : toTitleCase(address);

    const nextUserLocation = coordinates
      ? {
          coordinates,
          zip: zipFromGeolocation || (looksLikeZip ? digitsOnly.slice(0, 5) : null),
          radius: radiusMiles,
        }
      : null;
    setUserLocation(nextUserLocation);

    try {
      const resultData = await fetchPageData(1, address, nextUserLocation, newController.signal);
      
      if (searchRequestRef.current !== requestId) return;

      if (resultData.schools.length === 0) {
          throw new Error("We couldn't find any schools for that location.");
      }

      setSearchResults(resultData);
      pageCacheRef.current.set(1, resultData);
      setIsSearched(true);
      setIsLoading(false);

      const contextLat = nextUserLocation?.coordinates?.latitude ?? resultData?.coordinates?.[0];
      const contextLng = nextUserLocation?.coordinates?.longitude ?? resultData?.coordinates?.[1];
      const hasContext =
        Number.isFinite(Number(contextLat)) && Number.isFinite(Number(contextLng));

      if (hasContext) {
        setSearchContext({
          label,
          lat: Number(contextLat),
          lng: Number(contextLng),
          source,
          updatedAt: new Date().toISOString(),
        });
        setLocationError(null);
      } else {
        setSearchContext(null);
        setLocationError(
          "We couldn't locate that search. Showing national results."
        );
      }

    } catch (err) {
      if (
        err.name === "CanceledError" ||
        err.message === "canceled" ||
        err.message === "New search started"
      ) {
        return;
      }
      setError(err.message || "Failed to search schools.");
      setIsLoading(false);
    }
  };

  const handlePrefetchSearch = useCallback(async (rawQuery, options = {}) => {
    // Disabled for performance
  }, []);

  const loadStemSchools = useCallback(async () => {
    setStemLoading(true);
    setStemError(null);
    setCurrentPage(1);
    try {
      const response = await schoolAPI.getBestForStem();
      if (!response?.success || !response?.data)
        throw new Error("Could not load STEM schools.");
      const transformed = transformSchoolList(
        response.data.schools || [],
        "Best For STEM",
        { source: "stem", totalOverride: response.data.total_schools }
      );
      setStemResults(transformed);
    } catch (err) {
      setStemError(err.message);
    } finally {
      setStemLoading(false);
    }
  }, []);

  const loadAthletesSchools = useCallback(async () => {
    setAthletesLoading(true);
    setAthletesError(null);
    setCurrentPage(1);
    try {
      const response = await schoolAPI.getBestForAthletes();
      if (!response?.success || !response?.data)
        throw new Error("Could not load Athlete schools.");
      const transformed = transformSchoolList(
        response.data.schools || [],
        "Best For Athletes",
        { source: "athletes", totalOverride: response.data.total_schools }
      );
      setAthletesResults(transformed);
    } catch (err) {
      setAthletesError(err.message);
    } finally {
      setAthletesLoading(false);
    }
  }, []);

  const handleViewChange = (view) => {
    const requiresAuth = view === "stem" || view === "athletes";
    if (requiresAuth && !isSignedIn) {
      openAuthModal();
      return;
    }

    setActiveView(view);
    setCurrentPage(1);
    if (view === "stem" && !stemResults && !stemLoading) loadStemSchools();
    else if (view === "athletes" && !athletesResults && !athletesLoading)
      loadAthletesSchools();

    if (view !== "overview") {
      scrollToResults();
    }
  };

  const handleChangeLocation = () => {
    setActiveView("overview");
    scrollToSearch();
  };

  // Auto-scroll only on INITIAL search
  useEffect(() => {
    if (isLoading) return;
    if (!isSearched) return;
    if (!searchResults) return;
    if (autoScrolledRef.current) return;

    autoScrolledRef.current = true;
    scrollToResults();
  }, [isLoading, isSearched, searchResults]);

  useEffect(() => {
    if (isSignedIn && authModalOpen) {
      closeAuthModal();
    }
  }, [isSignedIn, authModalOpen, closeAuthModal]);

  const resultsSummary = useMemo(
    () =>
      searchResults
        ? { totalSchools: searchResults.totalSchools, query: searchResults.query }
        : null,
    [searchResults]
  );
  const stemSummary = useMemo(
    () =>
      stemResults
        ? { totalSchools: stemResults.totalSchools, query: stemResults.query }
        : null,
    [stemResults]
  );
  const athletesSummary = useMemo(
    () =>
      athletesResults
        ? { totalSchools: athletesResults.totalSchools, query: athletesResults.query }
        : null,
    [athletesResults]
  );

  const stemSchoolsSorted = useMemo(() => {
    const stemSchools = stemResults?.schools ?? [];
    if (!searchContext?.lat || !searchContext?.lng) return stemSchools;
    return addDistanceAndSort(stemSchools, searchContext.lat, searchContext.lng);
  }, [stemResults, searchContext?.lat, searchContext?.lng]);

  const athletesSchoolsSorted = useMemo(() => {
    const athletesSchools = athletesResults?.schools ?? [];
    if (!searchContext?.lat || !searchContext?.lng) return athletesSchools;
    return addDistanceAndSort(
      athletesSchools,
      searchContext.lat,
      searchContext.lng
    );
  }, [athletesResults, searchContext?.lat, searchContext?.lng]);

  const viewData = {
    overview: {
      error,
      loading: isLoading,
      refreshing: false,
      schools: searchResults?.schools ?? [],
      summary: resultsSummary,
      msg: "Searching schools...",
    },
    stem: {
      error: stemError,
      loading: stemLoading,
      refreshing: false,
      schools: stemSchoolsSorted,
      summary: stemSummary,
      msg: "Loading STEM schools...",
    },
    athletes: {
      error: athletesError,
      loading: athletesLoading,
      refreshing: false,
      schools: athletesSchoolsSorted,
      summary: athletesSummary,
      msg: "Loading Athlete schools...",
    },
  }[activeView];

  // --- PAGINATION ---
  const allSchools = viewData.schools;
  const estimatedTotal = viewData.summary?.totalSchools || allSchools.length;
  const totalPages = Math.max(1, Math.ceil(estimatedTotal / ITEMS_PER_PAGE));

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentDisplayedSchools =
    activeView === "overview" ? allSchools : allSchools.slice(startIndex, endIndex);

  const handlePageChange = async (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;

    setError(null);
    setCurrentPage(newPage);
    requestAnimationFrame(() => {
      scrollToResults();
    });

    // For non-overview views (stem/athletes), handle locally
    if (activeView !== "overview") {
      return;
    }

    if (!currentQuery) return;

    // Check cache first
    const cachedPage = pageCacheRef.current.get(newPage);
    if (cachedPage) {
      setSearchResults(cachedPage);
      setError(null);
      return;
    }

    // If not cached, fetch it
    setIsLoading(true);
    setError(null);

    try {
      const resultData = await fetchPageData(newPage, currentQuery, userLocation);
      setSearchResults(resultData);
      pageCacheRef.current.set(newPage, resultData);
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to load page.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F3F1] text-foreground">
      <Header
        activeView={activeView}
        onViewChange={handleViewChange}
        loginModalOpen={authModalOpen}
        onRequestLoginModal={openAuthModal}
        onCloseLoginModal={closeAuthModal}
      />

      <main className="pb-24 space-y-16">
        <HeroSection />

        <section id="search">
          <SearchSection
            onSearch={handleSearch}
            isSearched={isSearched}
            isLoading={isLoading}
            lastQuery={resultsSummary?.query ?? ""}
            activeView={activeView}
            onViewChange={handleViewChange}
            onPrefetchQuery={handlePrefetchSearch}
            onRequireAuth={openAuthModal}
          />
        </section>

        <section id="results-anchor" className="px-4 sm:px-8 lg:px-[133px]">
          {viewData.error && allSchools.length === 0 && (
            <div className="mb-6">
              <ErrorMessage
                message={viewData.error}
                onRetry={() => handleSearch(currentQuery)}
              />
            </div>
          )}

          {viewData.loading && allSchools.length === 0 ? (
            <div className="bg-white rounded-[32px] p-10">
              <LoadingSpinner message={viewData.msg} />
            </div>
          ) : (
            <>
              <SchoolsSection
                isSearched={activeView === "overview" ? isSearched : true}
                summary={viewData.summary}
                schools={currentDisplayedSchools}
                mode={activeView}
                activeLocation={searchContext}
                locationError={locationError}
                onChangeLocation={handleChangeLocation}
              />

              {estimatedTotal > 0 && (
                <div className="mt-8 mb-10 flex justify-center items-center gap-4">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-4 py-2 rounded-full border border-gray-300 bg-white text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    Previous
                  </button>

                  <span className="text-sm text-gray-600 font-medium">
                    Page {currentPage} of {totalPages}
                  </span>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 rounded-full bg-[#FF6B35] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#e85a2d] transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </section>

        <div className="px-4 sm:px-8 lg:px-[133px]">
          <MoreToolsToTry />
        </div>

        <CompareSection />
        <HomeSearchSection />
        <ShareStorySection />
        <ProcessSection />
      </main>

      <Footer />
    </div>
  );
}

export default App;
